import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X, Mail, KeyRound, Lock, ArrowRight, CheckCircle2 } from 'lucide-react';
import './ResetPasswordModal.css';

/**
 * ResetPasswordModal — 3-step forgot-password flow.
 *
 * Strict design notes:
 *   • Never asks for the old password (user has forgotten it).
 *   • Step 1: email → POST /api/auth/password-reset/request.
 *       Response is always 200 (anti-enumeration). We show a friendly
 *       "Reset request received. Please check your email — or contact
 *       support if the email service is unavailable." message and let
 *       the user proceed to Step 2 with the 6-digit code.
 *   • Step 2: code + new password + confirm new password.
 *   • Step 3: success state with direct CTA back to sign-in.
 *
 * Localised (EN / AR). RTL-safe — uses logical CSS properties and a
 * `dir`-aware root class.
 */
function ResetPasswordModal({ open, onClose, defaultEmail = '' }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(defaultEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [fieldErr, setFieldErr] = useState({});
  const [serverErr, setServerErr] = useState('');

  // Resend-code state. Cooldown lives in a single source of truth: a
  // wall-clock deadline (ms since epoch). The visible countdown is derived
  // from that deadline so React re-renders never "lose" seconds.
  // `resending` blocks rapid double-taps independently of the cooldown.
  // 600 s matches the server's per-email throttle (1 email / 10 min).
  const RESEND_COOLDOWN_SEC = 600;
  const [resendDeadline, setResendDeadline] = useState(0); // ms epoch
  const [now, setNow] = useState(() => Date.now());
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState('');

  // Drive `now` every second whenever there's an active cooldown. Cleanly
  // cancelled when the cooldown ends or the modal closes — no leaked
  // intervals, and the cooldown survives parent re-renders.
  React.useEffect(() => {
    if (resendDeadline <= now) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [resendDeadline, now]);

  const resendRemaining = Math.max(0, Math.ceil((resendDeadline - now) / 1000));

  // Hard reset on close so reopen starts clean.
  React.useEffect(() => {
    if (!open) {
      setStep(1);
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
      setSubmitting(false);
      setRequestSent(false);
      setFieldErr({});
      setServerErr('');
      setResendDeadline(0);
      setResending(false);
      setResendError('');
    } else {
      setEmail(defaultEmail || '');
    }
  }, [open, defaultEmail]);

  if (!open) return null;

  const submitRequest = async (e) => {
    e?.preventDefault();
    setFieldErr({});
    setServerErr('');
    const emailVal = (email || '').trim();
    if (!emailVal) {
      setFieldErr({ email: t('authEmailRequired') });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      setFieldErr({ email: t('authEmailInvalid') });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/password-reset/request`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: emailVal }),
        }
      );
      const data = await res.json().catch(() => ({}));
      // Response is always the same generic 200 (anti-enumeration) — the
      // backend never reveals registration or delivery status.
      if (!res.ok) {
        setServerErr(data?.message || t('resetRequestFailed'));
        return;
      }
      setRequestSent(true);
      // Start the resend cooldown mirroring the backend's 10-min per-email
      // throttle (server is source of truth).
      setResendDeadline(Date.now() + RESEND_COOLDOWN_SEC * 1000);
      setNow(Date.now());
      setResendError('');
      setStep(2);
    } catch (err) {
      setServerErr(t('resetRequestFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Re-send the reset code. Disabled while the deadline hasn't elapsed and
  // while a request is in flight. Even if the user manages to fire a request
  // early (e.g., DOM manipulation), the backend enforces the same 60 s
  // per-email cooldown and silently returns 200 → no enumeration surface.
  const requestResend = async () => {
    if (resending) return;
    if (resendRemaining > 0) return;
    const emailVal = (email || '').trim();
    if (!emailVal) return;

    setResending(true);
    setResendError('');
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/password-reset/request`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: emailVal }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResendError(data?.message || t('resetResendFailed'));
        return;
      }
      // Reset the visible cooldown only on a confirmed 2xx. Backend stays
      // the source of truth — if it silently swallowed (already cooling
      // down), our 60-s timer still matches its window.
      setResendDeadline(Date.now() + RESEND_COOLDOWN_SEC * 1000);
      setNow(Date.now());
    } catch (err) {
      setResendError(t('resetResendFailed'));
    } finally {
      setResending(false);
    }
  };

  const submitConfirm = async (e) => {
    e?.preventDefault();
    setFieldErr({});
    setServerErr('');

    const errs = {};
    const c = (code || '').trim();
    if (!c || !/^\d{6}$/.test(c)) {
      errs.code = t('resetCodeInvalid');
    }
    if (!newPassword) {
      errs.newPassword = t('authPasswordRequired');
    }
    if (newPassword !== confirmPassword) {
      errs.confirmPassword = t('passwordsDoNotMatch');
    }
    if (Object.keys(errs).length) {
      setFieldErr(errs);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/password-reset/confirm`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: (email || '').trim(),
            code: c,
            new_password: newPassword,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Surface the strong-password message verbatim — it's user-friendly.
        const detail =
          typeof data?.detail === 'string'
            ? data.detail
            : t('resetConfirmFailed');
        setServerErr(detail);
        return;
      }
      setStep(3);
      toast.success(t('resetSuccess'));
    } catch (err) {
      setServerErr(t('resetConfirmFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="reset-modal-overlay"
      role="dialog"
      aria-modal="true"
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="reset-modal-overlay"
      onClick={(e) => {
        if (e.target.classList.contains('reset-modal-overlay')) onClose();
      }}
    >
      <div className="reset-modal" data-testid="reset-modal">
        <button
          type="button"
          className="reset-modal-close"
          aria-label={t('close')}
          onClick={onClose}
          data-testid="reset-modal-close"
        >
          <X size={18} />
        </button>

        {step === 1 && (
          <form onSubmit={submitRequest} className="reset-modal-form">
            <div className="reset-icon-wrap">
              <Mail size={26} />
            </div>
            <h2 className="reset-modal-title">{t('forgotPassword')}</h2>
            <p className="reset-modal-sub">{t('resetStep1Sub')}</p>

            <label className="reset-field-label" htmlFor="reset-email">
              {t('emailLabel')}
            </label>
            <input
              id="reset-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`reset-input${fieldErr.email ? ' reset-input--err' : ''}`}
              placeholder={t('emailPlaceholder')}
              data-testid="reset-email-input"
              autoFocus
            />
            {fieldErr.email ? (
              <p className="reset-field-error" data-testid="reset-email-error">
                {fieldErr.email}
              </p>
            ) : null}

            {serverErr ? (
              <p className="reset-server-error" data-testid="reset-server-error">
                {serverErr}
              </p>
            ) : null}

            <button
              type="submit"
              className="reset-primary-btn"
              disabled={submitting}
              data-testid="reset-request-submit"
            >
              {submitting ? t('loading') : t('resetSendCode')}
              <ArrowRight size={16} className="reset-btn-arrow" />
            </button>

            <button
              type="button"
              className="reset-link-btn"
              onClick={() => setStep(2)}
              data-testid="reset-already-have-code"
            >
              {t('resetAlreadyHaveCode')}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitConfirm} className="reset-modal-form">
            <div className="reset-icon-wrap">
              <KeyRound size={26} />
            </div>
            <h2 className="reset-modal-title">{t('resetStep2Title')}</h2>
            <p className="reset-modal-sub">
              {requestSent
                ? t('resetStep2SubSent', { email })
                : t('resetStep2Sub')}
            </p>

            <label className="reset-field-label" htmlFor="reset-code">
              {t('resetCodeLabel')}
            </label>
            <input
              id="reset-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className={`reset-input reset-input--code${fieldErr.code ? ' reset-input--err' : ''}`}
              placeholder="000000"
              data-testid="reset-code-input"
              autoFocus
            />
            {fieldErr.code ? (
              <p className="reset-field-error" data-testid="reset-code-error">
                {fieldErr.code}
              </p>
            ) : null}

            {/* Resend code — disabled during the 60s cooldown. The
                button has a fixed min-width so the countdown digits don't
                jiggle the layout. */}
            <div className="reset-resend-row" data-testid="reset-resend-row">
              <button
                type="button"
                className="reset-resend-btn"
                onClick={requestResend}
                disabled={resending || resendRemaining > 0}
                data-testid="reset-resend-btn"
                aria-live="polite"
              >
                {resendRemaining > 0
                  ? t('resetResendIn', { count: resendRemaining })
                  : resending
                    ? t('loading')
                    : t('resetResendCode')}
              </button>
            </div>
            {resendError ? (
              <p className="reset-field-error" data-testid="reset-resend-error">
                {resendError}
              </p>
            ) : null}

            <label className="reset-field-label" htmlFor="reset-new-password">
              {t('resetNewPassword')}
            </label>
            <input
              id="reset-new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={`reset-input${fieldErr.newPassword ? ' reset-input--err' : ''}`}
              placeholder={t('resetNewPasswordPlaceholder')}
              data-testid="reset-new-password-input"
            />
            {fieldErr.newPassword ? (
              <p className="reset-field-error" data-testid="reset-new-password-error">
                {fieldErr.newPassword}
              </p>
            ) : null}

            <label className="reset-field-label" htmlFor="reset-confirm-password">
              {t('resetConfirmPassword')}
            </label>
            <input
              id="reset-confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`reset-input${fieldErr.confirmPassword ? ' reset-input--err' : ''}`}
              placeholder={t('resetConfirmPasswordPlaceholder')}
              data-testid="reset-confirm-password-input"
            />
            {fieldErr.confirmPassword ? (
              <p className="reset-field-error" data-testid="reset-confirm-password-error">
                {fieldErr.confirmPassword}
              </p>
            ) : null}

            {serverErr ? (
              <p className="reset-server-error" data-testid="reset-server-error">
                {serverErr}
              </p>
            ) : null}

            <p className="reset-policy-hint">{t('resetPasswordPolicy')}</p>

            <button
              type="submit"
              className="reset-primary-btn"
              disabled={submitting}
              data-testid="reset-confirm-submit"
            >
              {submitting ? t('loading') : t('resetConfirmCta')}
              <Lock size={16} className="reset-btn-arrow" />
            </button>

            <button
              type="button"
              className="reset-link-btn"
              onClick={() => setStep(1)}
              data-testid="reset-back-to-email"
            >
              {t('resetBackToEmail')}
            </button>
          </form>
        )}

        {step === 3 && (
          <div className="reset-modal-form reset-success">
            <div className="reset-icon-wrap reset-icon-wrap--ok">
              <CheckCircle2 size={32} />
            </div>
            <h2 className="reset-modal-title">{t('resetSuccessTitle')}</h2>
            <p className="reset-modal-sub">{t('resetSuccessSub')}</p>
            <button
              type="button"
              className="reset-primary-btn"
              onClick={onClose}
              data-testid="reset-done-btn"
            >
              {t('resetGoToLogin')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordModal;
